import React, { useState } from 'react'
import { adCreativeService, useGetBusinessID } from '@marketing-milk/frontend'
import { AdCreative, LeadCard } from '@marketing-milk/interfaces'
import { Drawer } from '@mui/material'
import { EmptyLeadCardId } from 'components'
import { Step } from 'components/Stepper/Step'
import { Stepper } from 'components/Stepper/Stepper'
import { toast } from 'react-toastify'
import { AssignLeadFormsStep } from './AssignLeadFormsStep'
import { DeclareLeadFormsStep } from './DeclareLeadFormsStep'

interface Props {
  isOpen: boolean
  onClose: () => void
  campaignName?: string
  creatives?: AdCreative[]
  onAssigned: () => void | Promise<unknown>
  // Held by the sprint page rather than here so the declared list survives the drawer closing
  // and reopening — including forms declared but not yet assigned to anything, which nothing in
  // the creatives can reconstruct. Undefined until the AE first changes the list. It lives as
  // long as the sprint page does and no longer: re-declaring after a refresh is cheap.
  rememberedForms?: LeadCard[]
  rememberForms: (forms: LeadCard[]) => void
}

const dedupeById = (forms: LeadCard[]): LeadCard[] => {
  const seen = new Set<number>()
  return forms.filter(form => {
    if (seen.has(form.id)) return false
    seen.add(form.id)
    return true
  })
}

export const EditLeadFormsModal = ({
  isOpen,
  onClose,
  campaignName,
  creatives,
  onAssigned,
  rememberedForms,
  rememberForms,
}: Props) => {
  const businessID = useGetBusinessID()
  const [selectedCreatives, setSelectedCreatives] = useState<AdCreative[]>([])
  // Until the AE changes anything, the declared list is just whatever the batch's creatives
  // already carry — they shouldn't have to re-declare what's already true. Once they do change
  // it, their version stands: recomputing from the creatives would resurrect a form they just
  // removed, in the window before the refetched creatives come back without it.
  const declaredForms =
    rememberedForms ??
    dedupeById(
      (creatives ?? []).map(c => c.leadCardUsed).filter((form): form is LeadCard => Boolean(form))
    )
  const [activeFormId, setActiveFormId] = useState(EmptyLeadCardId)
  const [assigning, setAssigning] = useState(false)
  const [unassigning, setUnassigning] = useState(false)
  const [sorting, setSorting] = useState(false)
  const busy = assigning || unassigning || sorting
  const [creatingForm, setCreatingForm] = useState(false)
  const [formActionsContainer, setFormActionsContainer] = useState<HTMLDivElement | null>(null)

  const selectCreative = (creative: AdCreative) => {
    if (busy) return
    const alreadySelected = selectedCreatives.some(c => c.id === creative.id)
    setSelectedCreatives(
      alreadySelected
        ? selectedCreatives.filter(c => c.id !== creative.id)
        : [...selectedCreatives, creative]
    )
  }

  const selectAll = (visibleCreatives: AdCreative[]) => {
    if (busy) return
    const selectedIds = new Set(selectedCreatives.map(c => c.id))
    setSelectedCreatives([
      ...selectedCreatives,
      ...visibleCreatives.filter(c => !selectedIds.has(c.id)),
    ])
  }

  const deselectAll = (visibleCreatives: AdCreative[]) => {
    if (busy) return
    const visibleIds = new Set(visibleCreatives.map(c => c.id))
    setSelectedCreatives(selectedCreatives.filter(c => !visibleIds.has(c.id)))
  }

  const handleClose = () => {
    if (busy) return
    setSelectedCreatives([])
    onClose()
  }

  // The endpoint applies to every assignable creative and reports the rest by name rather than
  // rejecting the whole batch — one published ad must not block moving the other nineteen. This
  // surfaces exactly which ones didn't land, since the rail's counts alone won't say why.
  const notifyFailures = (failed: Array<{ name: string }>, verb: 'reassigned' | 'unassigned') => {
    if (!failed.length) return
    toast.error(
      `${failed.length} published ad${failed.length === 1 ? '' : 's'} couldn't be ${verb}: ${failed
        .map(f => f.name)
        .join(', ')}`
    )
  }

  // Removing a declared form is global, not scoped to this campaign — it clears the FK on
  // every ad currently carrying it, everywhere. DeclareLeadFormsStep gates this behind a
  // confirmation that names the count before calling it.
  const unassignForm = async (form: LeadCard) => {
    const affected = (creatives ?? []).filter(c => c.leadCardUsed?.id === form.id)
    if (affected.length === 0) {
      onAssigned()
      return
    }
    try {
      const { failed } = await adCreativeService.bulkAssignLeadCard(
        Number(businessID),
        affected.map(c => c.id),
        EmptyLeadCardId
      )
      notifyFailures(failed, 'unassigned')
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    }
    onAssigned()
  }

  const assignToSelected = async () => {
    if (busy || activeFormId === EmptyLeadCardId || selectedCreatives.length === 0) return

    setAssigning(true)
    try {
      const { updated, failed } = await adCreativeService.bulkAssignLeadCard(
        Number(businessID),
        selectedCreatives.map(c => c.id),
        activeFormId
      )
      if (updated.length) {
        toast.success(
          `Lead form assigned to ${updated.length} ad${updated.length === 1 ? '' : 's'}`
        )
      }
      notifyFailures(failed, 'reassigned')
      await onAssigned()

      // Only the ones that didn't land stay selected, so a retry or the undo button acts on
      // exactly what's still outstanding rather than the whole original selection.
      const failedIds = new Set(failed.map(f => f.id))
      setSelectedCreatives(selectedCreatives.filter(c => failedIds.has(c.id)))
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    } finally {
      setAssigning(false)
    }
  }

  // Undo for a bad bulk assignment — select the affected ads and send just those back to
  // Unassigned, rather than the all-or-nothing "remove the whole declared form" in step one.
  const unassignSelected = async () => {
    if (busy) return
    const toUnassign = selectedCreatives.filter(c => c.leadCardUsed)
    if (toUnassign.length === 0) return

    setUnassigning(true)
    try {
      const { updated, failed } = await adCreativeService.bulkAssignLeadCard(
        Number(businessID),
        toUnassign.map(c => c.id),
        EmptyLeadCardId
      )
      if (updated.length) {
        toast.success(
          `Lead form removed from ${updated.length} ad${updated.length === 1 ? '' : 's'}`
        )
      }
      notifyFailures(failed, 'unassigned')
      await onAssigned()

      const failedIds = new Set(failed.map(f => f.id))
      setSelectedCreatives(selectedCreatives.filter(c => failedIds.has(c.id)))
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    } finally {
      setUnassigning(false)
    }
  }

  // Scope follows the selection, and falls back to every unassigned ad when there is none —
  // hand-place the few that are obvious, then let this take the rest. A selection that includes
  // already-assigned ads is overwritten on purpose: ticking one is how you say "redo this one".
  const aiSort = async () => {
    if (busy) return
    const toSort = selectedCreatives.length
      ? selectedCreatives
      : (creatives ?? []).filter(c => !c.leadCardUsed)
    if (toSort.length === 0 || declaredForms.length === 0) return

    setSorting(true)
    try {
      const { updated, failed, skipped } = await adCreativeService.aiSortLeadCards(
        Number(businessID),
        toSort.map(c => c.id),
        declaredForms.map(f => f.id)
      )
      if (updated.length) {
        toast.success(
          `Lead forms sorted onto ${updated.length} ad${updated.length === 1 ? '' : 's'}`
        )
      }
      notifyFailures(failed, 'reassigned')
      if (skipped.length) {
        toast.warn(
          `AI couldn't match ${skipped.length} ad${skipped.length === 1 ? '' : 's'}. ` +
            `Their lead forms were left unchanged; review: ${skipped.map(c => c.name).join(', ')}`
        )
      }
      const updatedIds = new Set(updated.map(c => c.id))
      setSelectedCreatives(selected => selected.filter(c => !updatedIds.has(c.id)))
      await onAssigned()
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    } finally {
      setSorting(false)
    }
  }

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <fieldset
        disabled={busy}
        aria-busy={busy}
        className="w-screen xl:w-[80vw] max-w-full min-w-0 border-0 p-0 m-0"
      >
        <Stepper
          submitButtonText="Done"
          footer={
            creatingForm ? (
              <footer className="flex-none border-t bg-white px-4 py-2">
                <div ref={setFormActionsContainer} />
              </footer>
            ) : undefined
          }
        >
          <Step
            title="Declare Forms"
            canContinue={declaredForms.length > 0}
            disabled={creatingForm}
          >
            <DeclareLeadFormsStep
              businessID={Number(businessID)}
              campaignName={campaignName}
              creatives={creatives ?? []}
              declaredForms={declaredForms}
              setDeclaredForms={rememberForms}
              onUnassignForm={unassignForm}
              onBuildingChange={setCreatingForm}
              actionsContainer={formActionsContainer}
            />
          </Step>
          <Step title="Assign Ads" onNext={handleClose}>
            <AssignLeadFormsStep
              campaignName={campaignName}
              creatives={creatives}
              declaredForms={declaredForms}
              activeFormId={activeFormId}
              setActiveFormId={setActiveFormId}
              selectedCreatives={selectedCreatives}
              selectCreative={selectCreative}
              selectAll={selectAll}
              deselectAll={deselectAll}
              onSelectLeadless={selectAll}
              onAssign={assignToSelected}
              assigning={assigning}
              onUnassign={unassignSelected}
              unassigning={unassigning}
              onAISort={aiSort}
              sorting={sorting}
            />
          </Step>
        </Stepper>
      </fieldset>
    </Drawer>
  )
}
